import QRCode from "qrcode";

const PHOMEMO_SERVICE_UUIDS = [
  0xff00,
  "0000ff00-0000-1000-8000-00805f9b34fb",
];

const WRITE_CHARACTERISTIC_UUIDS = [
  0xff02,
  "0000ff02-0000-1000-8000-00805f9b34fb",
];

// =========================================================
// PHOMEMO M220 - ETIKETA FIZIKE
// =========================================================
// M220 punon me 203 DPI ≈ 8 dots/mm.
// 50 mm × 30 mm = 400 × 240 dots.
// Këto dimensione përdoren si dimensione REALE të rasterit
// që i dërgohet printerit me Bluetooth.
const LABEL_WIDTH_MM = 50;
const LABEL_HEIGHT_MM = 30;
const DOTS_PER_MM = 8;

const PRINT_WIDTH_PX = LABEL_WIDTH_MM * DOTS_PER_MM;   // 400 dots
const LABEL_HEIGHT_PX = LABEL_HEIGHT_MM * DOTS_PER_MM; // 240 dots;

// Paketa më të vogla janë zakonisht më stabël në BLE.
const DEFAULT_CHUNK_SIZE = 96;
const CHUNK_DELAY_MS = 12;

let cachedDevice = null;
let cachedWriteCharacteristic = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export const isWebBluetoothSupported = () =>
  typeof navigator !== "undefined" &&
  "bluetooth" in navigator &&
  !isIOS();

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Etiketa nuk mund të gjenerohej."));
    }, "image/png");
  });

const fitText = (ctx, text, maxWidth, startSize, minSize = 14) => {
  let size = startSize;

  while (size > minSize) {
    ctx.font = `700 ${size}px Arial, Helvetica, sans-serif`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 1;
  }

  return size;
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

const getRepairLabelData = (repair) => {
  const firstName = firstValue(
    repair?.first_name,
    repair?.firstname,
    repair?.client_first_name,
    repair?.customer_first_name,
    repair?.name
  );

  const lastName = firstValue(
    repair?.last_name,
    repair?.lastname,
    repair?.client_last_name,
    repair?.customer_last_name,
    repair?.surname
  );

  const explicitFullName = firstValue(
    repair?.customer_name,
    repair?.client_name,
    repair?.full_name
  );

  const fullName =
    explicitFullName ||
    [firstName, lastName].filter(Boolean).join(" ").trim();

  const device = firstValue(
    repair?.phone_type,
    repair?.device,
    repair?.device_name,
    repair?.device_model,
    repair?.model,
    repair?.product_name,
    repair?.phone_model,
    repair?.device_type
  );

  const phone = firstValue(
    repair?.phone,
    repair?.phone_number,
    repair?.telephone,
    repair?.tel,
    repair?.client_phone,
    repair?.customer_phone,
    repair?.mobile
  );

  return {
    fullName: fullName || "Klient i panjohur",
    device: device || "Pajisje e papërcaktuar",
    phone: phone || "-",
  };
};

const createQrMatrix = async (value) => {
  const qrCanvas = document.createElement("canvas");

  await QRCode.toCanvas(qrCanvas, value, {
    errorCorrectionLevel: "H",
    margin: 4,
    width: 220,
    color: {
      dark: "#000000",
      light: "#ffffff",
    },
  });

  return qrCanvas;
};

/**
 * QR 3D i skanueshëm:
 * - matrica reale QR mbetet e zezë/bardhë dhe nuk deformohet;
 * - efekti 3D krijohet me hije shumë të çelët (#d9d9d9);
 * - threshold-i i printerit është 155, ndaj kjo hije nuk printohet si e zezë;
 * - quiet-zone 4 module ruhet e paprekur.
 */
const drawScannable3DQr = (ctx, qrCanvas, x, y, size) => {
  const temp = document.createElement("canvas");
  temp.width = qrCanvas.width;
  temp.height = qrCanvas.height;

  const tctx = temp.getContext("2d", { willReadFrequently: true });
  tctx.drawImage(qrCanvas, 0, 0);

  const image = tctx.getImageData(0, 0, temp.width, temp.height);
  const data = image.data;

  // Gjej kufijtë realë të moduleve të zeza, por jo quiet-zone.
  let minX = temp.width;
  let minY = temp.height;
  let maxX = 0;
  let maxY = 0;

  for (let py = 0; py < temp.height; py += 1) {
    for (let px = 0; px < temp.width; px += 1) {
      const i = (py * temp.width + px) * 4;
      if (data[i] < 40 && data[i + 1] < 40 && data[i + 2] < 40) {
        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
        maxX = Math.max(maxX, px);
        maxY = Math.max(maxY, py);
      }
    }
  }

  // Në rast të pazakontë, vizato QR normal.
  if (minX > maxX || minY > maxY) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrCanvas, x, y, size, size);
    return;
  }

  const srcW = maxX - minX + 1;
  const srcH = maxY - minY + 1;

  // Quiet-zone nga QR origjinal ruhet duke vizatuar fillimisht sfondin e plotë.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, size, size);

  // Hije/ekstrudim i lehtë. Është aq i çelët sa nuk bëhet modul i zi në raster.
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#d9d9d9";

  // Vizato disa kopje të maskës së moduleve vetëm si hije djathtas-poshtë.
  // destination-over e mban hijen pas faqes së zezë.
  ctx.globalCompositeOperation = "source-over";
  ctx.imageSmoothingEnabled = false;

  const shadowCanvas = document.createElement("canvas");
  shadowCanvas.width = temp.width;
  shadowCanvas.height = temp.height;
  const sctx = shadowCanvas.getContext("2d", { willReadFrequently: true });
  const shadowImage = sctx.createImageData(temp.width, temp.height);

  for (let p = 0; p < data.length; p += 4) {
    const black = data[p] < 40 && data[p + 1] < 40 && data[p + 2] < 40;
    if (black) {
      shadowImage.data[p] = 217;
      shadowImage.data[p + 1] = 217;
      shadowImage.data[p + 2] = 217;
      shadowImage.data[p + 3] = 255;
    } else {
      shadowImage.data[p + 3] = 0;
    }
  }

  sctx.putImageData(shadowImage, 0, 0);

  // Thellësi vizuale 2–3 px në etiketën finale.
  ctx.drawImage(shadowCanvas, x + 3, y + 3, size, size);
  ctx.drawImage(shadowCanvas, x + 2, y + 2, size, size);
  ctx.restore();

  // Faqja kryesore: QR standard, pa deformim dhe pa blur.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qrCanvas, x, y, size, size);

  // Highlight i bardhë shumë i hollë në cepin sipër-majtas për ndjesinë 3D.
  // Nuk prek strukturën e moduleve sepse vizatohet vetëm në bordurën e jashtme.
  ctx.save();
  ctx.strokeStyle = "#f4f4f4";
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);
  ctx.restore();
};

export const createServiceLabelCanvas = async (repair) => {
  const serviceCode = String(repair?.service_code || "").trim();

  if (!serviceCode) {
    throw new Error("Ky servis nuk ka kod QR.");
  }

  // Canvas i etiketës
  const canvas = document.createElement("canvas");
  canvas.width = PRINT_WIDTH_PX;
  canvas.height = LABEL_HEIGHT_PX;

  const ctx = canvas.getContext("2d", {
    willReadFrequently: true,
  });

  // Sfond i bardhë
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // QR
  const qrCanvas = await createQrMatrix(serviceCode);

  // Mbaje 150 sepse më the që kështu skanonte mirë
  const qrSize = 150;

  const qrX = Math.floor((canvas.width - qrSize) / 2);
  const qrY = 20;

  drawScannable3DQr(
    ctx,
    qrCanvas,
    qrX,
    qrY,
    qrSize
  );

  // Kodi i servisit poshtë QR
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const codeSize = fitText(
    ctx,
    serviceCode,
    canvas.width - 40,
    20,
    15
  );

  ctx.font = `700 ${codeSize}px Arial, Helvetica, sans-serif`;

  ctx.fillText(
    serviceCode,
    canvas.width / 2,
    200
  );

  return canvas;
};

const canvasToRaster = (canvas) => {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;

  const widthBytes = Math.ceil(width / 8);
  const raster = new Uint8Array(widthBytes * height);

  // Threshold pak më i fortë për kontrast më të pastër në printer termik.
  const threshold = 155;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixelIndex = (y * width + x) * 4;
      const r = imageData[pixelIndex];
      const g = imageData[pixelIndex + 1];
      const b = imageData[pixelIndex + 2];
      const alpha = imageData[pixelIndex + 3];

      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      const isBlack = alpha > 32 && luminance < threshold;

      if (isBlack) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        raster[byteIndex] |= 0x80 >> (x % 8);
      }
    }
  }

  return {
    data: raster,
    widthBytes,
    heightLines: height,
  };
};

const getSafeChunkSize = (characteristic) => {
  // BLE implementations ndryshojnë sipas browser/device.
  // 96 është konservative dhe zakonisht stabile.
  if (characteristic?.properties?.writeWithoutResponse) {
    return DEFAULT_CHUNK_SIZE;
  }

  return 64;
};

const writeChunked = async (characteristic, bytes) => {
  const chunkSize = getSafeChunkSize(characteristic);

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.slice(
      offset,
      Math.min(offset + chunkSize, bytes.length)
    );

    const buffer = chunk.buffer.slice(
      chunk.byteOffset,
      chunk.byteOffset + chunk.byteLength
    );

    if (
      characteristic.properties?.writeWithoutResponse &&
      typeof characteristic.writeValueWithoutResponse === "function"
    ) {
      await characteristic.writeValueWithoutResponse(buffer);
    } else if (typeof characteristic.writeValueWithResponse === "function") {
      await characteristic.writeValueWithResponse(buffer);
    } else {
      await characteristic.writeValue(buffer);
    }

    await delay(CHUNK_DELAY_MS);
  }
};

const clearCachedPrinter = () => {
  cachedWriteCharacteristic = null;

  if (cachedDevice && !cachedDevice.gatt?.connected) {
    cachedDevice = null;
  }
};

const findWriteCharacteristic = async (service) => {
  let lastError = null;

  for (const uuid of WRITE_CHARACTERISTIC_UUIDS) {
    try {
      const characteristic = await service.getCharacteristic(uuid);

      if (
        characteristic.properties?.write ||
        characteristic.properties?.writeWithoutResponse
      ) {
        return characteristic;
      }
    } catch (error) {
      lastError = error;
    }
  }

  // Fallback: kërko automatikisht një characteristic që lejon write.
  try {
    const characteristics = await service.getCharacteristics();

    const writable = characteristics.find(
      (item) =>
        item.properties?.write ||
        item.properties?.writeWithoutResponse
    );

    if (writable) return writable;
  } catch (error) {
    lastError = error;
  }

  throw new Error(
    `Nuk u gjet characteristic për shkrim te printeri. ${
      lastError?.message || ""
    }`.trim()
  );
};

const connectKnownDevice = async () => {
  if (!cachedDevice) return null;

  if (!cachedDevice.gatt) {
    clearCachedPrinter();
    return null;
  }

  if (!cachedDevice.gatt.connected) {
    await cachedDevice.gatt.connect();
  }

  return cachedDevice;
};

const requestPhomemoDevice = async () => {
  // requestDevice kërkon klikim të përdoruesit në browser.
  return navigator.bluetooth.requestDevice({
    filters: [
      { namePrefix: "M220" },
      { namePrefix: "Phomemo" },
      { namePrefix: "M" },
    ],
    optionalServices: PHOMEMO_SERVICE_UUIDS,
  });
};

const getWriteCharacteristic = async () => {
  if (
    cachedDevice?.gatt?.connected &&
    cachedWriteCharacteristic
  ) {
    return cachedWriteCharacteristic;
  }

  let device = null;

  try {
    device = await connectKnownDevice();
  } catch {
    clearCachedPrinter();
  }

  if (!device) {
    device = await requestPhomemoDevice();
    cachedDevice = device;
  }

  const server = device.gatt.connected
    ? device.gatt
    : await device.gatt.connect();

  let service = null;
  let lastError = null;

  for (const serviceUuid of PHOMEMO_SERVICE_UUIDS) {
    try {
      service = await server.getPrimaryService(serviceUuid);
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!service) {
    throw new Error(
      `Nuk u gjet shërbimi Bluetooth i Phomemo M220. ${
        lastError?.message || ""
      }`.trim()
    );
  }

  cachedWriteCharacteristic = await findWriteCharacteristic(service);

  device.addEventListener(
    "gattserverdisconnected",
    () => {
      cachedWriteCharacteristic = null;
    },
    { once: true }
  );

  return cachedWriteCharacteristic;
};

const buildRasterHeader = (widthBytes, heightLines) =>
  new Uint8Array([
    0x1d,
    0x76,
    0x30,
    0x00,
    widthBytes & 0xff,
    (widthBytes >> 8) & 0xff,
    heightLines & 0xff,
    (heightLines >> 8) & 0xff,
  ]);

const printCanvasViaBluetooth = async (canvas) => {
  const characteristic = await getWriteCharacteristic();
  const { data, widthBytes, heightLines } = canvasToRaster(canvas);

  const init = new Uint8Array([0x1b, 0x40]);

  // Ruajmë parametrat që po përdorje, por dërgimi është bërë më robust.
  const heatSettings = new Uint8Array([
    0x1b, 0x37, 0x07, 0x78, 0x02
  ]);

  const density = new Uint8Array([
    0x1d, 0x7c, 0x06
  ]);

  const feed = new Uint8Array([
    0x1b, 0x4a, 0x18
  ]);

  await writeChunked(characteristic, init);
  await delay(80);

  await writeChunked(characteristic, heatSettings);
  await delay(20);

  await writeChunked(characteristic, density);
  await delay(30);

  await writeChunked(
    characteristic,
    buildRasterHeader(widthBytes, heightLines)
  );

  await writeChunked(characteristic, data);

  await delay(220);
  await writeChunked(characteristic, feed);
  await delay(350);
};

const openBrowserPrintFallback = async (canvas) => {
  const blob = await canvasToBlob(canvas);
  const imageUrl = URL.createObjectURL(blob);

  const printWindow = window.open(
    "",
    "_blank",
    "width=700,height=500"
  );

  if (!printWindow) {
    URL.revokeObjectURL(imageUrl);
    throw new Error(
      "Browser-i bllokoi dritaren e printimit. Lejo pop-up për këtë faqe."
    );
  }

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Barcode servisi</title>

        <style>
          @page {
            size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm;
            margin: 0 !important;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            width: ${LABEL_WIDTH_MM}mm !important;
            height: ${LABEL_HEIGHT_MM}mm !important;
            min-width: ${LABEL_WIDTH_MM}mm !important;
            min-height: ${LABEL_HEIGHT_MM}mm !important;
            max-width: ${LABEL_WIDTH_MM}mm !important;
            max-height: ${LABEL_HEIGHT_MM}mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          body {
            position: relative;
          }

          img {
            position: absolute;
            inset: 0;
            display: block;
            width: ${LABEL_WIDTH_MM}mm !important;
            height: ${LABEL_HEIGHT_MM}mm !important;
            max-width: none !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 0 !important;
            object-fit: fill;
            image-rendering: pixelated;
          }

          @media print {
            html,
            body {
              width: ${LABEL_WIDTH_MM}mm !important;
              height: ${LABEL_HEIGHT_MM}mm !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          }
        </style>
      </head>

      <body>
        <img src="${imageUrl}" alt="Barcode servisi" />

        <script>
          const img = document.querySelector("img");

          const doPrint = () => {
            window.focus();

            setTimeout(() => {
              window.print();
            }, 100);
          };

          if (img.complete) {
            doPrint();
          } else {
            img.onload = doPrint;
          }

          window.onafterprint = () => {
            window.close();
          };
        <\/script>
      </body>
    </html>
  `);

  printWindow.document.close();

  setTimeout(() => {
    URL.revokeObjectURL(imageUrl);
  }, 60000);
};

const shareLabelOnIOS = async (canvas, serviceCode) => {
  const blob = await canvasToBlob(canvas);

  const file = new File(
    [blob],
    `${serviceCode}.png`,
    { type: "image/png" }
  );

  // iPhone/iPad nuk mbështet Web Bluetooth në Safari.
  // Prandaj përdorim Share Sheet që përdoruesi ta hapë etiketën
  // në Phomemo / Print / Files pa kaluar te A4/A3/B5.
  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      title: `Etiketa ${serviceCode}`,
      text: "Etiketë servisi 50 × 30 mm për Phomemo M220",
      files: [file],
    });

    return {
      mode: "ios-share",
      direct: false,
      widthMm: LABEL_WIDTH_MM,
      heightMm: LABEL_HEIGHT_MM,
    };
  }

  // Fallback për iOS/browser që nuk lejon file sharing:
  // hap vetëm imazhin e etiketës, jo një faqe A4.
  const imageUrl = URL.createObjectURL(blob);
  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    URL.revokeObjectURL(imageUrl);
    throw new Error(
      "Browser-i bllokoi dritaren e etiketës. Lejo pop-up dhe provo përsëri."
    );
  }

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1" />
        <title>${serviceCode}</title>
        <style>
          * { box-sizing: border-box; }
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            min-height: 100%;
            background: #fff;
          }
          body {
            display: flex;
            align-items: flex-start;
            justify-content: center;
          }
          img {
            display: block;
            width: 100%;
            max-width: 500px;
            height: auto;
            object-fit: contain;
            image-rendering: pixelated;
          }
        </style>
      </head>
      <body>
        <img src="${imageUrl}" alt="${serviceCode}" />
      </body>
    </html>
  `);

  printWindow.document.close();

  setTimeout(() => {
    URL.revokeObjectURL(imageUrl);
  }, 60000);

  return {
    mode: "ios-open",
    direct: false,
    widthMm: LABEL_WIDTH_MM,
    heightMm: LABEL_HEIGHT_MM,
  };
};

/**
 * Lidhu me printerin paraprakisht.
 * Mund ta thërrasësh nga një buton "Lidh printerin".
 * Pasi lidhet një herë gjatë sesionit, printimet pasuese bëhen me një klik.
 */
export const connectPhomemoM220 = async () => {
  if (!isWebBluetoothSupported()) {
    return {
      connected: false,
      unsupported: true,
      mode: "system-print",
      deviceName: null,
    };
  }

  const characteristic = await getWriteCharacteristic();

  if (!characteristic) {
    throw new Error("Nuk u arrit lidhja me printerin.");
  }

  return {
    connected: true,
    unsupported: false,
    mode: "bluetooth",
    deviceName: cachedDevice?.name || "Phomemo M220",
  };
};

export const disconnectPhomemoM220 = () => {
  try {
    if (cachedDevice?.gatt?.connected) {
      cachedDevice.gatt.disconnect();
    }
  } finally {
    cachedDevice = null;
    cachedWriteCharacteristic = null;
  }
};


export const getPhomemoM220DeviceInfo = () => ({
  connected: Boolean(
    cachedDevice?.gatt?.connected &&
    cachedWriteCharacteristic
  ),
  name: cachedDevice?.name || null,
  id: cachedDevice?.id || null,
});

export const isPhomemoM220Connected = () =>
  Boolean(
    cachedDevice?.gatt?.connected &&
    cachedWriteCharacteristic
  );

export const getPhomemoLabelSize = () => ({
  widthMm: LABEL_WIDTH_MM,
  heightMm: LABEL_HEIGHT_MM,
  dotsPerMm: DOTS_PER_MM,
  widthDots: PRINT_WIDTH_PX,
  heightDots: LABEL_HEIGHT_PX,
});

export const printServiceBarcodeSystem = async (repair) => {
  if (repair?.status !== "pending") {
    throw new Error(
      "Barcode-i mund të printohet vetëm kur servisi është në pritje."
    );
  }

  const canvas = await createServiceLabelCanvas(repair);
  await openBrowserPrintFallback(canvas);

  return {
    mode: "system-print",
    direct: false,
    deviceName: null,
  };
};

export const printServiceBarcodeM220 = async (repair) => {
  if (repair?.status !== "pending") {
    throw new Error(
      "Barcode-i mund të printohet vetëm kur servisi është në pritje."
    );
  }

  if (!repair?.service_code) {
    throw new Error("Ky servis nuk ka kod QR.");
  }

  const canvas = await createServiceLabelCanvas(repair);

  if (
    canvas.width !== PRINT_WIDTH_PX ||
    canvas.height !== LABEL_HEIGHT_PX
  ) {
    throw new Error(
      `Dimensioni i etiketës nuk është korrekt. Pritet ${PRINT_WIDTH_PX}×${LABEL_HEIGHT_PX} dots.`
    );
  }

  // ANDROID / DESKTOP CHROME / EDGE:
  // printim direkt BLE te M220, pa dialog printimi.
  if (isWebBluetoothSupported()) {
    if (!isPhomemoM220Connected()) {
      throw new Error(
        'Phomemo M220 nuk është lidhur. Shtyp fillimisht "Lidh printerin Bluetooth".'
      );
    }

    try {
      await printCanvasViaBluetooth(canvas);

      return {
        mode: "bluetooth",
        direct: true,
        widthMm: LABEL_WIDTH_MM,
        heightMm: LABEL_HEIGHT_MM,
        widthDots: PRINT_WIDTH_PX,
        heightDots: LABEL_HEIGHT_PX,
        deviceName: cachedDevice?.name || "Phomemo M220",
      };
    } catch (error) {
      console.error("PHOMEMO DIRECT PRINT ERROR:", error);

      throw new Error(
        `Printimi direkt te ${
          cachedDevice?.name || "Phomemo M220"
        } dështoi. ${error?.message || ""}`.trim()
      );
    }
  }

  // IPHONE / IPAD:
  // Safari nuk jep BLE direkt; hap Share Sheet me PNG 50×30.
  if (isIOS()) {
    return await shareLabelOnIOS(canvas, repair.service_code);
  }

  // Browser tjetër pa Web Bluetooth:
  // përdor printimin e sistemit si fallback.
  await openBrowserPrintFallback(canvas);

  return {
    mode: "system-print",
    direct: false,
    widthMm: LABEL_WIDTH_MM,
    heightMm: LABEL_HEIGHT_MM,
  };
};
